import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationScoping } from './useOrganizationScoping';
import { toast } from '@/hooks/use-toast';

export interface PendingMembership {
  id: string;
  user_id: string;
  org_id: string;
  requested_at: string;
  status: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  // Joined profile data
  user_profile?: {
    name?: string;
    email?: string;
    phone_number?: string;
  };
}

export const usePendingMemberships = () => {
  const [pendingMemberships, setPendingMemberships] = useState<PendingMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const { organizationId } = useOrganizationScoping();

  useEffect(() => {
    if (organizationId) {
      fetchPendingMemberships();
    }
  }, [organizationId]);

  const fetchPendingMemberships = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('pending_memberships')
        .select(`
          *,
          user_profile:profiles!fk_pending_memberships_user(name, email, phone_number)
        `)
        .eq('org_id', organizationId)
        .order('requested_at', { ascending: false });

      if (error) throw error;
      
      const formattedData = (data || []).map(item => ({
        ...item,
        user_profile: Array.isArray(item.user_profile) ? item.user_profile[0] : item.user_profile
      })) as PendingMembership[];
      
      setPendingMemberships(formattedData);
    } catch (error) {
      console.error('Error fetching pending memberships:', error);
      toast({
        title: "Error",
        description: "Failed to load pending memberships",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const approveMembership = async (pendingId: string, notes?: string) => {
    try {
      setProcessing(true);
      
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) throw new Error('Not authenticated');

      // Get current user's profile ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', currentUser.user.id)
        .single();
      
      if (!profile) throw new Error('Profile not found');

      const { data, error } = await supabase.rpc('approve_pending_membership', {
        p_pending_id: pendingId,
        p_approved_by: profile.id,
        p_notes: notes
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string } | null;
      if (result?.success) {
        toast({
          title: "Success",
          description: "Membership approved successfully",
        });
        await fetchPendingMemberships();
      } else {
        throw new Error(result?.error || 'Failed to approve membership');
      }
    } catch (error) {
      console.error('Error approving membership:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to approve membership",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const rejectMembership = async (pendingId: string, notes?: string) => {
    try {
      setProcessing(true);
      
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) throw new Error('Not authenticated');

      // Get current user's profile ID
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', currentUser.user.id)
        .single();
      
      if (!profile) throw new Error('Profile not found');

      const { data, error } = await supabase.rpc('reject_pending_membership', {
        p_pending_id: pendingId,
        p_rejected_by: profile.id,
        p_notes: notes
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string } | null;
      if (result?.success) {
        toast({
          title: "Success",
          description: "Membership rejected",
        });
        await fetchPendingMemberships();
      } else {
        throw new Error(result?.error || 'Failed to reject membership');
      }
    } catch (error) {
      console.error('Error rejecting membership:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reject membership",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const bulkApprove = async (pendingIds: string[]) => {
    try {
      setProcessing(true);
      
      for (const id of pendingIds) {
        await approveMembership(id);
      }
      
      toast({
        title: "Success",
        description: `Approved ${pendingIds.length} memberships`,
      });
    } catch (error) {
      console.error('Error bulk approving memberships:', error);
      toast({
        title: "Error",
        description: "Some memberships could not be approved",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const bulkReject = async (pendingIds: string[], notes?: string) => {
    try {
      setProcessing(true);
      
      for (const id of pendingIds) {
        await rejectMembership(id, notes);
      }
      
      toast({
        title: "Success",
        description: `Rejected ${pendingIds.length} memberships`,
      });
    } catch (error) {
      console.error('Error bulk rejecting memberships:', error);
      toast({
        title: "Error",
        description: "Some memberships could not be rejected",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return {
    pendingMemberships,
    loading,
    processing,
    approveMembership,
    rejectMembership,
    bulkApprove,
    bulkReject,
    refetch: fetchPendingMemberships,
  };
};